import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { PATHS } from './constants/paths';

import AuthPage from './pages/auth/AuthPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import { HomePage } from './pages/home/HomePage';
import { ProfilePage } from './pages/profile/ProfilePage';
import TreePage from './pages/tree/TreePage';
import TreeDetailPage from './pages/tree-detail/TreeDetailPage';
import PaymentPage from './pages/payment/PaymentPage';
import CapsuleCreatePage from './pages/capsule-create/CapsuleCreatePage';
import { CapsuleDetailPage } from './pages/capsule-detail/CapsuleDetailPage';
import { UserProfilePage } from './pages/user-profile/UserProfilePage';
import FAQPage from './pages/faq/FAQPage';
import AboutPage from './pages/about/AboutPage';
import MyCapsulesPage from './pages/my-capsules/MyCapsulesPage';
import ChatPage from './pages/chat/ChatPage';
import ChatInvitePage from './pages/chat/ChatInvitePage';
import TermsPage from './pages/terms/TermsPage';
import W_NotFound from './components/W_Wrappers/W_NotFound/W_NotFound';

export const ROUTES = {
  [PATHS.HOME]: {
    component: HomePage,
    protected: true,
    withWrapper: true,
  },
  [PATHS.AUTH]: {
    component: AuthPage,
    protected: false,
    withWrapper: false,
  },
  [PATHS.CAPSULE_CREATE]: {
    component: CapsuleCreatePage,
    protected: true,
    withWrapper: false,
  },
  [PATHS.CAPSULES]: {
    component: MyCapsulesPage,
    protected: true,
    withWrapper: true,
  },
  [PATHS.CHAT]: {
    component: ChatPage,
    protected: true,
    withWrapper: false,
  },
  [PATHS.FAQ]: {
    component: FAQPage,
    protected: false,
    withWrapper: false,
  },
  [PATHS.ABOUT]: {
    component: AboutPage,
    protected: false,
    withWrapper: false,
  },
  [PATHS.TERMS]: {
    component: TermsPage,
    protected: false,
    withWrapper: false,
  },
  [PATHS.PAYMENT]: {
    component: PaymentPage,
    protected: true,
    withWrapper: false,
  },
  [PATHS.PAYMENT_SUBSCRIPTION]: {
    component: PaymentPage,
    protected: true,
    withWrapper: false,
  },
  [PATHS.CAPSULE_DONATION]: {
    component: PaymentPage,
    protected: true,
    withWrapper: false,
  },
} as const;

const ProfileWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  if (!user) {
    return <Navigate to={PATHS.AUTH_REGISTER} replace />;
  }

  const handleBack = () => {
    navigate(PATHS.HOME);
  };

  const handleLogout = () => {
    signOut();
    navigate(PATHS.AUTH);
  };

  return (
    <ProfilePage user={user} onBack={handleBack} onLogout={handleLogout} />
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user, signOut, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={PATHS.AUTH_REGISTER} replace />;
  }

  return (
    <HomePage
      user={user}
      onLogout={() => {
        signOut();
      }}
    />
  );
};

const StandaloneProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={PATHS.AUTH_REGISTER} replace />;
  }

  return <>{children}</>;
};

const UserProfileWrapper: React.FC = () => {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  if (!user) {
    return <Navigate to={PATHS.AUTH_REGISTER} replace />;
  }

  const handleLogout = () => {
    signOut();
    navigate(PATHS.AUTH);
  };

  return <UserProfilePage currentUser={user} onLogout={handleLogout} />;
};

export const AppRouter: React.FC = () => {
  return (
    <Routes>
      <Route
        path={PATHS.HOME}
        element={
          <ProtectedRoute>
            <div />
          </ProtectedRoute>
        }
      />

      <Route path={PATHS.AUTH} element={<AuthPage />} />
      <Route path={PATHS.VERIFY_EMAIL} element={<VerifyEmailPage />} />
      <Route path={PATHS.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={PATHS.RESET_PASSWORD} element={<ResetPasswordPage />} />

      <Route
        path={PATHS.PROFILE}
        element={
          <StandaloneProtectedRoute>
            <ProfileWrapper />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.TREE}
        element={
          <StandaloneProtectedRoute>
            <TreePage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.TREE_BY_ID}
        element={
          <StandaloneProtectedRoute>
            <TreeDetailPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CAPSULE_CREATE}
        element={
          <StandaloneProtectedRoute>
            <CapsuleCreatePage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CAPSULE_EDIT}
        element={
          <StandaloneProtectedRoute>
            <CapsuleCreatePage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CAPSULES}
        element={
          <StandaloneProtectedRoute>
            <MyCapsulesPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CHAT}
        element={
          <StandaloneProtectedRoute>
            <ChatPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CHAT_INVITE}
        element={
          <StandaloneProtectedRoute>
            <ChatInvitePage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CHAT_CONVERSATION}
        element={
          <StandaloneProtectedRoute>
            <ChatPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route path={PATHS.CAPSULE} element={<CapsuleDetailPage />} />

      <Route
        path={PATHS.USER_PROFILE}
        element={
          <StandaloneProtectedRoute>
            <UserProfileWrapper />
          </StandaloneProtectedRoute>
        }
      />

      <Route path={PATHS.FAQ} element={<FAQPage />} />
      <Route path={PATHS.ABOUT} element={<AboutPage />} />
      <Route path={PATHS.TERMS} element={<TermsPage />} />

      <Route
        path={PATHS.PAYMENT}
        element={
          <StandaloneProtectedRoute>
            <PaymentPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.PAYMENT_SUBSCRIPTION}
        element={
          <StandaloneProtectedRoute>
            <PaymentPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route
        path={PATHS.CAPSULE_DONATION}
        element={
          <StandaloneProtectedRoute>
            <PaymentPage />
          </StandaloneProtectedRoute>
        }
      />

      <Route path='*' element={<W_NotFound />} />
    </Routes>
  );
};
